import {
    MILITARY_VARIANT,
    STC_VARIANT,
    collection,
    isMilitaryEnabled,
    parentTypeFor,
} from "../_variants/index.js";
import {
    addDocument,
    cancelDocument,
    changeLinkedRevision,
    checkDocument,
    checkDocuments,
    editDocument,
    generateDocument,
    linkDocument,
    makePrototype,
    reviseDocument,
    reviseDrawingToA,
    revisionRollback,
    unlinkDocument,
    updateUploadedStatus,
} from "./actions";
import { appConfig, isPrototypeRevision } from "../../../utils";

import {
    get
} from "lodash";
import store from "../../../store";

function warning(key) {
    let item = store.state[key];
    if (!item) return false;
    if(item.Cancelled) return false;

    let warningMessage = "";
    if (!item?.Revisions) return "No revision's found";

    if (!item?.Revisions[item.RevisionID]?.Quality?.Approver?.Completed && !item.Cancelled) {
        warningMessage = warningMessage += `${item.Number} REV ${item.Revision} not complete.<br>`;
    }
    if (isPrototypeRevision(item.Revision)) {
        warningMessage = warningMessage += `${item.Number} at a prototype revision.<br>`;
    }
    return warningMessage === "" ? false : warningMessage;
}

const cellClassRules = {
    "text-orange font-bold": params => {
        return warning(params.data);
    }
}

function linkedRevisionCompleted(params) {
    const linkedRevision = get(params.data, params.column.colDef.field, params.data.RevisionID);
    return get(params.data, `Revisions.${linkedRevision}.Quality.Approver.Completed`, false);
}

/**
 * Build the attachable-table descriptor for the Document register that
 * matches the supplied variant. Row actions rely on the parent's
 * `meta.ParentType` to derive the document's variant at action time.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeDocumentsAttachable = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military Document" : "Document";
    const labelPlural = isMilitary ? "Military Documents" : "Documents";
    const docParentType = parentTypeFor(variant, "Documents");
    const docCollection = collection(variant, "Documents");

    const actions = [{
        "label": `Add ${labelSingular}`,
        "action": addDocument,
        show({ meta }) {
            return [["DesignChanges", "FlightConditions", "Documents", "Repairs", "MilitaryDesignChanges", "MilitaryFlightConditions", "MilitaryDocuments", "MilitaryRepairs"].includes(meta.ParentType), `New ${labelPlural} cannot be added to this, link an existing ${labelSingular}`]
        }
    }, {
        "label": `Link ${labelSingular}`,
        "action": linkDocument
    }];

    const headers = [{
        "headerName": "Number",
        "field": "Number",
        "sort": "asc",
        cellClassRules
    }, {
        "headerName": "Title",
        "field": "Title",
        cellClassRules
    }, {
        "headerName": "Linked Revision",
        cellClassRules,
        fieldGetter({ pageItem }) {
            return `Links.${pageItem.ID}`
        },
        valueFormatter(params) {
            let linkedRev = get(params.data, params.column.colId);
            let revision = get(params.data, `Revisions.${linkedRev}`);
            if (!!revision) {
                return revision.Revision === params.data.Revision ? revision.Revision : `${revision.Revision} (Revision ${params.data.Revision} Available)`
            } else return params.data.Revision
        }
    }, {
        "headerName": "Revision Completed",
        "type": "bool",
        fieldGetter({ pageItem }) {
            return `Links.${pageItem.ID}`
        },
        valueGetter: linkedRevisionCompleted,
        valueFormatter: linkedRevisionCompleted
    }];

    const rowActions = [{
        "label": `View ${labelSingular} Details`,
        action({ router, rowItem }) {
            router.push(`/Database/${docParentType}/${rowItem.ID}`)
        }
    }, {
        "label": `Edit ${labelSingular}`,
        "action": editDocument,
        "break": true
    }, {
        "label": `Revise ${labelSingular}`,
        "action": reviseDocument,
        show({ item }) {
            return [item.Revisions[item.RevisionID]?.Quality?.Approver?.Completed === true, "Revision not completed"];
        }
    }, {
        "label": "Rollback Revision",
        "action": revisionRollback,
        show({ item }) {
            return [Object.keys(item?.Revisions || {}).length > 1, "No previous revisions found"];
        }
    }, {
        "label": "Change Linked Revision",
        "action": changeLinkedRevision,
        show({ item }) {
            return [Object.keys(item?.Revisions || {}).length > 1, "No previous revisions found"];
        }
    }, {
        "label": "Revise to Prototype",
        "action": makePrototype,
        "break": true,
        show({ item }) {
            return isPrototypeRevision(item.Revision) ? [false, "Revision already a prototype"] : [true, ""];
        }
    }, {
        "label": "Revise from Prototype",
        "action": reviseDrawingToA,
        show({ item }) {
            return isPrototypeRevision(item.Revision) ? [true, ""] : [false, "Revision not at prototype"];
        }
    }, {
        "label": "Set Revision as Complete",
        "action": checkDocument("Approver"),
        "break": true,
        show({ item, store }) {

            if(item.Type === "EAF") {
                return [!!item.PartNumbers, "Part Numbers not set"]
            }
            return [item.Revisions[item.RevisionID].Quality.Approver.Completed === false, "Revision already completed"];
        }
    }, {
        "label": `Unlink ${labelSingular}`,
        "break": true,
        "action": unlinkDocument,
        show({ rowItem, pageItem }) {
            return [rowItem.ParentID !== pageItem.ID, "Cannot unlink from the main parent"]
        }
    }, {
        "label": "AI Tool - Document Check",
        "action": checkDocuments
    }, {
        "label": `Generate ${labelSingular}`,
        "break": true,
        "action": generateDocument,
        "show": ({ item, store }) => {
            if(!store.state["Database/DocumentTypes/"]) {
                return [false, "Document Types not loaded"];
            }
            const type = store.state["Database/DocumentTypes/"][item?.TypeID];

            if(!type) return [false, "Document Type not found"];
            if(!type.Revisions) return [false, "No revisions found"];

            const typeRevision = type.Revisions[type.RevisionID];
            if(!typeRevision) {
                return [false, "Document Type revision not found"];
            }

            if(!typeRevision.GeneratableFile) {
                return [false, "Generatable file not uploaded"];
            }

            const currentRevision = item.Revisions?.[item.RevisionID];
            if(!currentRevision) {
                return [false, "Current revision not found"];
            }

            const approverCompleted = currentRevision.Quality?.Approver?.Completed;
            const approverBy = currentRevision.Quality?.Approver?.By;

            if(approverCompleted !== true) {
                return [false, "Approver sign-off not completed"];
            }

            if(!approverBy || approverBy === "N/A" || approverBy === "") {
                return [false, "Approver sign-off required"];
            }

            return [true, ""];
        }
    },{
        "label": `Cancel ${labelSingular}`,
        "action": cancelDocument,
        "class": "font-bold text-red",
        "break": true
    }];

    if(appConfig.appPrefix !== "stc") {
        rowActions.splice(rowActions.length - 2, 0, {
            "label": "Update Upload Status",
            "action": updateUploadedStatus,
            "break": true
        });

        headers.push({
            "headerName": "Uploaded Status",
            "field": "UploadStatus",
            valueFormatter: params => {
                return params?.data?.UploadStatus || "Not Uploaded";
            },
            cellClassRules
        });

        headers.push({ 
            "headerName": "Uploaded Deadline",
            fieldGetter({ pageItem }) {
                return "DocumentUploadDeadline";
            },
            valueFormatter: params => {
                const uploadStatus = params?.data?.UploadStatus;
                if(uploadStatus === "N/A") return "N/A";

                
                const deadline = params?.data?.DocumentUploadDeadline;
                if (!deadline) return "Document Upload Deadline not set";


                if(uploadStatus !== "Not Uploaded") return "Uploaded";

                const deadlineDate = deadline ? new Date(deadline) : null;
                const daysUtilDeadline = deadlineDate ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                const isOverdue = daysUtilDeadline !== null && daysUtilDeadline < 0;

                if(isOverdue) return "<span class='text-red'>Overdue</span>";
                return `Due in ${daysUtilDeadline} days`;
            },
            cellClassRules: {
      
                "text-green": params => {
                    const formattedValue = params.colDef.valueFormatter(params);
                    return formattedValue === "Uploaded";
                },
                "text-red": params => {
                    const formattedValue = params.colDef.valueFormatter(params);
                    return formattedValue.includes("Overdue");
                },
                "text-orange": params => {
                    const formattedValue = params.colDef.valueFormatter(params);
                    return formattedValue === "Document Upload Deadline not set";
                }
            }

        });
    };

    return {
        "label": labelPlural,
        "key": docParentType,
        "location": `${docCollection}/`,
        actions,
        rowActions,
        headers,
        warn({ items }) {
            let warningMessage = items.map(warning).filter(i => i !== false).join("<br>");
            return [warningMessage !== "", warningMessage];
        }
    };
};

export const documentsAttachable = makeDocumentsAttachable(STC_VARIANT);
export const militaryDocumentsAttachable = isMilitaryEnabled() ? makeDocumentsAttachable(MILITARY_VARIANT) : null;

export default documentsAttachable;
